// src/components/portal/EmployeeDocuments.js
import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { useAuth } from '../../auth/AuthContext';
import BusinessCentralService from '../../services/businessCentralService';
import PdfPreviewModal from '../common/PdfPreviewModal';
import FiscalYearSelectionModal from '../common/FiscalYearSelectionModal';
import { 
  FileText, Download, Eye, Filter, Calendar, ChevronDown,
  FileDigit, RefreshCw, Clock
} from 'lucide-react';

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB');
  } catch {
    return dateString;
  }
};

const EmployeeDocuments = () => {
  const { user, getAccessToken, activeEnvironment } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentPdf, setCurrentPdf] = useState(null);
  const [currentPdfTitle, setCurrentPdfTitle] = useState('');
  const [activeTab, setActiveTab] = useState('payslips');
  const [payslips, setPayslips] = useState([]);
  const [taxDocuments, setTaxDocuments] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [fiscalYearFilterOptions, setFiscalYearFilterOptions] = useState([]);
  
  // Fiscal year selection modal state
  const [fiscalYearModalOpen, setFiscalYearModalOpen] = useState(false);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [fiscalYearsLoading, setFiscalYearsLoading] = useState(false);
  const [pendingP60Action, setPendingP60Action] = useState(null); // Store the action (view/download) and doc

  // Initialize Business Central service — only recreate when activeEnvironment changes,
  // NOT when getAccessToken changes reference (it changes every render and causes infinite loops)
  const bcService = React.useMemo(() => {
    return new BusinessCentralService(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnvironment]);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user || !activeEnvironment) return;

      try {
        setIsLoading(true);
        
        // Fetch payslips first
        const payslipData = await bcService.getPayslips();
        setPayslips(payslipData || []);

        // Fetch fiscal years independently — a failure here must not block payslip rendering
        let fiscalYearOptions = [];
        try {
          const fiscalYearsResponse = await bcService.getFiscalYears();
          if (fiscalYearsResponse.success && fiscalYearsResponse.data.length > 0) {
            fiscalYearOptions = fiscalYearsResponse.data;
          }
        } catch (fyError) {
          logger.error('Fiscal years API failed, falling back to payslip data:', fyError);
        }

        // Fall back to fiscal years derived from payslip data if API returned nothing
        if (fiscalYearOptions.length === 0) {
          const getFY = (p) => {
            if (p.fiscalYear) return p.fiscalYear;
            const d = new Date(p.expectedPaymentDate || p.payDate);
            if (isNaN(d.getTime())) return null;
            const yr = d.getFullYear(), mo = d.getMonth() + 1, dy = d.getDate();
            const start = (mo > 4 || (mo === 4 && dy >= 6)) ? yr : yr - 1;
            return `${start}/${start + 1}`;
          };
          const uniqueFiscalYears = [...new Set((payslipData || []).map(getFY).filter(Boolean))].sort().reverse();
          fiscalYearOptions = uniqueFiscalYears.map(fy => ({ fiscalYear: fy, active: false }));
        }

        setFiscalYearFilterOptions(fiscalYearOptions);
        if (fiscalYearOptions.length > 0) {
          // Default to the active fiscal year, otherwise the first one
          const defaultFY = fiscalYearOptions.find(fy => fy.active) || fiscalYearOptions[0];
          setSelectedFiscalYear(defaultFY.fiscalYear);
        }

        // Fetch employee profile to get validP11D
        const employeeProfile = await bcService.getEmployeeProfile();
        const validP11D = employeeProfile?.validP11D || false;
        
        // One entry per document type — year selection happens via the FiscalYearSelectionModal
        const taxDocs = [
          {
            id: 'p60',
            systemId: 'p60',
            type: 'P60',
            description: 'End of Year Certificate'
          },
          {
            id: 'p11d',
            systemId: 'p11d',
            type: 'P11D',
            description: 'Expenses and Benefits Statement'
          }
        ];
        
        // Filter out P11D documents if validP11D is false
        const filteredTaxDocs = validP11D ? taxDocs : taxDocs.filter(doc => doc.type !== 'P11D');
        
        setTaxDocuments(filteredTaxDocs);
        
      } catch (error) {
        logger.error('Error fetching documents:', error?.message || error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDocuments();
  }, [user, activeEnvironment]); // bcService excluded — it depends on activeEnvironment already

  // State for error messages
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Load fiscal years for P60 selection
  const loadFiscalYears = async () => {
    try {
      setFiscalYearsLoading(true);
      const response = await bcService.getFiscalYears();
      
      if (response.success) {
        setFiscalYears(response.data);
      } else {
        logger.error('Error loading fiscal years:', response.error);
        setErrorMessage(`Failed to load fiscal years: ${response.error}`);
        setFiscalYears([]);
      }
    } catch (error) {
      logger.error('Error loading fiscal years:', error?.message || error);
      setErrorMessage('Failed to load fiscal years');
      setFiscalYears([]);
    } finally {
      setFiscalYearsLoading(false);
    }
  };
  
  // Handle fiscal year selection for P60 and P11D
  const handleFiscalYearSelection = async (selectedFiscalYear) => {
    try {
      if (!pendingP60Action) {
        logger.error('No pending document action found');
        return;
      }
      
      const { doc, type, employeeGuid } = pendingP60Action;
      
      setIsLoadingPdf(true);
      
      // Use the selected fiscal year as taxYear
      const taxYear = selectedFiscalYear.fiscalYear;
      
      logger.log(`${doc.type} API call with selected fiscal year:`, { employeeGuid, taxYear });
      
      // Get document with the selected tax year based on document type
      let response;
      if (doc.type === 'P60') {
        response = await bcService.getP60Pdf(employeeGuid, taxYear);
      } else if (doc.type === 'P11D') {
        response = await bcService.getP11DPdf(employeeGuid, taxYear);
      } else {
        throw new Error(`Unsupported document type: ${doc.type}`);
      }
      
      logger.log(`${doc.type} API response:`, response);
      
      // Handle the response same as other document types
      if (response.success && response.data) {
        if (type === 'download') {
          // Convert base64 to Blob and download
          const byteCharacters = atob(response.data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${doc.type}_${selectedFiscalYear.fiscalYear}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          // Show in modal
          setCurrentPdf(response.data);
          setCurrentPdfTitle(`${doc.type} - ${selectedFiscalYear.fiscalYear}`);
          setPdfModalOpen(true);
        }
      } else {
        const errorMsg = response.error || `Failed to retrieve ${doc.type} document`;
        logger.error(`${doc.type} retrieval error:`, errorMsg);
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      logger.error(`Error processing ${pendingP60Action?.doc?.type || 'document'} with fiscal year:`, error?.message || error);
      setErrorMessage(`Failed to process ${pendingP60Action?.doc?.type || 'document'} document`);
    } finally {
      setIsLoadingPdf(false);
      setPendingP60Action(null);
    }
  };
  
  const handleDocumentView = async (doc, type = 'view') => {
    if (!doc) {
      logger.error('No document provided');
      return;
    }

    try {
      // Clear any previous error messages
      setErrorMessage(null);
      setIsLoadingPdf(true);
      
      // Get employee data from localStorage
      const employeeData = JSON.parse(localStorage.getItem('employeeData') || '{}');
      
      // For P60 API, we need the employee GUID (id), not employeeNo
      const employeeGuid = employeeData.id || localStorage.getItem('employeeId');
      const employeeNo = employeeData.employeeNo || localStorage.getItem('employeeNo');
      
      // Response variable that will hold our document data or error
      let response;
      
      if (activeTab === 'payslips') {
        // For payslips, use the payslip PDF API (uses systemId, not employee ID)
        if (!doc.systemId) {
          logger.error('No payslip systemId provided');
          setErrorMessage('Payslip ID is missing');
          return;
        }
        
        // Legacy API returns base64 directly, not structured response
        try {
          const pdfData = await bcService.getPayslipPdf(doc.systemId);
          if (!pdfData) {
            throw new Error('No payslip data returned from API');
          }
          response = { success: true, data: pdfData, error: null };
        } catch (error) {
          logger.error('Error fetching payslip PDF:', error?.message || error);
          response = { success: false, data: null, error: error.message || 'Failed to retrieve payslip' };
        }
      } else if (doc.type === 'P60') {
        // For P60, first show fiscal year selection modal
        if (!employeeGuid) {
          logger.error('No employee GUID available for P60 document');
          setErrorMessage('Employee GUID is required but not available');
          return;
        }
        
        // Store the pending action and document for after fiscal year selection
        setPendingP60Action({ doc, type, employeeGuid });
        
        // Load fiscal years and show modal
        setIsLoadingPdf(false); // Stop loading indicator here as modal will take over
        await loadFiscalYears();
        setFiscalYearModalOpen(true);
        return; // Exit early as the modal will handle the rest
      } else if (doc.type === 'P11D') {
        // For P11D, first show fiscal year selection modal
        if (!employeeGuid) {
          logger.error('No employee GUID available for P11D document');
          setErrorMessage('Employee GUID is required but not available');
          return;
        }
        
        // Store the pending action and document for after fiscal year selection
        setPendingP60Action({ doc, type, employeeGuid });
        
        // Load fiscal years and show modal
        setIsLoadingPdf(false); // Stop loading indicator here as modal will take over
        await loadFiscalYears();
        setFiscalYearModalOpen(true);
        return; // Exit early as the modal will handle the rest
      } else {
        logger.error('Unknown document type:', doc.type);
        setErrorMessage(`Unknown document type: ${doc.type}`);
        return;
      }
      
      // Check if the API call was successful
      if (!response.success) {
        logger.error('Document retrieval failed:', response.error);
        setErrorMessage(response.error || 'Failed to retrieve document');
        return;
      }
      
      // Get the PDF data
      const pdfData = response.data;
      
      if (!pdfData) {
        logger.error('No PDF data returned');
        setErrorMessage('No document data returned');
        return;
      }
      
      // Generate document title
      const documentTitle = doc.type 
        ? `${doc.type} - ${doc.taxYear || doc.period}` 
        : `Payslip - ${doc.period}`;
      
      if (type === 'view') {
        // Show in the modal
        setCurrentPdf(pdfData);
        setCurrentPdfTitle(documentTitle);
        setPdfModalOpen(true);
      } else if (type === 'download') {
        // Convert base64 to Blob for download
        const byteCharacters = atob(pdfData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create URL for the blob
        const blobUrl = URL.createObjectURL(blob);
        
        // Determine filename
        let filename;
        if (activeTab === 'payslips') {
          filename = `Payslip-${doc.period.replace(/\s+/g, '-')}.pdf`;
        } else {
          filename = `${doc.type}-${doc.taxYear.replace(/\s+/g, '-')}.pdf`;
        }
        
        // Create download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      }
    } catch (error) {
      logger.error('Error handling document PDF:', error?.message || error);
      setErrorMessage('An error occurred while processing your request');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  // Derive UK fiscal year string from a date (UK tax year: 6 Apr – 5 Apr)
  const getFiscalYearFromDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const startYear = (month > 4 || (month === 4 && day >= 6)) ? year : year - 1;
    return `${startYear}/${startYear + 1}`;
  };

  const renderPayslips = () => {
    // Filter payslips by selected fiscal year.
    // Uses payslip.fiscalYear if present, otherwise derives it from expectedPaymentDate.
    const filteredPayslips = selectedFiscalYear
      ? payslips.filter(payslip => {
          const fy = payslip.fiscalYear || getFiscalYearFromDate(payslip.expectedPaymentDate || payslip.payDate);
          return fy === selectedFiscalYear;
        })
      : payslips;

    if (filteredPayslips.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
            <FileText className="h-8 w-8 text-indigo-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Payslips Found</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            No payslips are available for the selected fiscal year. Try selecting a different year or contact your administrator.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Payslips</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Pay</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Pay</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayslips.map((payslip) => (
                <tr key={payslip.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payslip.period}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(payslip.expectedPaymentDate || payslip.payDate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{(payslip.grossPay || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{(payslip.taxAmount ?? payslip.taxPaid ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">£{(payslip.netPay || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                    <div className="flex items-center justify-center space-x-3">
                      <button 
                        onClick={() => handleDocumentView(payslip, 'view')}
                        disabled={isLoadingPdf}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors disabled:opacity-50"
                        title="View payslip"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => handleDocumentView(payslip, 'download')}
                        disabled={isLoadingPdf}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors disabled:opacity-50"
                        title="Download payslip"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTaxDocuments = () => {
    const filteredDocuments = taxDocuments;
    
    if (filteredDocuments.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
            <FileDigit className="h-8 w-8 text-indigo-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Tax Documents Found</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            No tax documents are available for the selected year. Tax documents like P60s are typically issued annually at the end of the tax year, and P45s when you leave employment.
          </p>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Tax Documents</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Type</th>               
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDocuments.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-indigo-100">
                        <FileDigit className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{doc.type}</div>
                      </div>
                    </div>
                  </td>                 
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                    <div className="flex items-center justify-center space-x-3">
                      <button 
                        onClick={() => handleDocumentView(doc, 'view')}
                        disabled={isLoadingPdf}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors disabled:opacity-50"
                        title={`View ${doc.type}`}
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => handleDocumentView(doc, 'download')}
                        disabled={isLoadingPdf}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors disabled:opacity-50"
                        title={`Download ${doc.type}`}
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfData={currentPdf}
        title={currentPdfTitle}
      />
      
      {/* Fiscal Year Selection Modal */}
      <FiscalYearSelectionModal
        isOpen={fiscalYearModalOpen}
        onClose={() => {
          setFiscalYearModalOpen(false);
          setPendingP60Action(null);
        }}
        onSelect={handleFiscalYearSelection}
        fiscalYears={fiscalYears}
        isLoading={fiscalYearsLoading}
        title={`Select Fiscal Year for ${pendingP60Action?.doc?.type || 'Document'}`}
        documentType={pendingP60Action?.doc?.type}
      />
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Documents</h1>
          <p className="mt-1 text-gray-500">Access and manage your employment documents</p>
        </div>                        
      </div>
      
      {/* Error Message Notification */}
      {errorMessage && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-md p-4 mb-6 shadow-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1 md:flex md:justify-between">
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Document Not Available</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{errorMessage}</p>
                </div>
              </div>
              <div className="ml-6 pl-1">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={() => setErrorMessage(null)}
                    className="inline-flex p-1.5 text-yellow-500 rounded-md hover:bg-yellow-100 focus:outline-none"
                    aria-label="Dismiss"
                  >
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payslips'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } transition-colors`}
            onClick={() => setActiveTab('payslips')}
          >
            <div className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              <span>Payslips</span>
            </div>
          </button>
          
          <button
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tax'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } transition-colors`}
            onClick={() => setActiveTab('tax')}
          >
            <div className="flex items-center">
              <FileDigit className="h-5 w-5 mr-2" />
              <span>Tax Documents</span>
            </div>
          </button>
        </nav>
      </div>
      
      {/* Fiscal Year Filter — always shown on payslips tab */}
      {activeTab === 'payslips' && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3 flex items-center gap-3">
          <Calendar className="h-5 w-5 text-indigo-500 flex-shrink-0" />
          <label htmlFor="fiscalYearSelect" className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Fiscal Year
          </label>
          <select
            id="fiscalYearSelect"
            value={selectedFiscalYear || ''}
            onChange={e => setSelectedFiscalYear(e.target.value || null)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {fiscalYearFilterOptions.length === 0 ? (
              <option value="">Loading...</option>
            ) : (
              fiscalYearFilterOptions.map(option => (
                <option key={option.fiscalYear} value={option.fiscalYear}>
                  {option.fiscalYear}{option.active ? ' (Active)' : ''}
                </option>
              ))
            )}
          </select>
        </div>
      )}

      {/* Content based on active tab */}
      {activeTab === 'payslips' && renderPayslips()}
      {activeTab === 'tax' && renderTaxDocuments()}
      
      {/* Information Panel */}
      <div className="bg-blue-50 rounded-lg p-4 mt-8">
        <div className="flex">
          <div className="flex-shrink-0">
            <Clock className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Secure Access to Payroll Documents</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
              This portal provides controlled and secure access to your payslips, tax forms, and other payroll-related documents. 
              Files are not stored within the portal itself but are securely retrieved in real time from Dynamics 365 Business Central. 
              Access is restricted to authorized users, and all data transmission adheres to GDPR and data protection standards, including encryption in transit.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDocuments;